import { Fragment, FunctionComponent } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useAppDispatch } from '../../reducers/root';
import { graphql, RelayProp, useFragment } from 'react-relay';
import { achFormUpdated, toggleBillingAddress } from '../../actions/checkoutPaymentActions';
import { isVerifyingBankAccount } from '../../reducers/checkoutPaymentReducer';
import { useCheckoutSelector } from '../../reducers/root';
import styles from '../styles/CheckoutACHForm.scss';

import { Checkbox } from 'dibs-elements/exports/Checkbox';
import CheckoutStripeForm from './CheckoutStripeForm';
import { ControlledAddressForm } from 'dibs-address-form';
import CheckoutEoriVatForm from '../../components/forms/CheckoutEoriVatForm';
import { CheckoutACHFormStripe_viewer$key } from './__generated__/CheckoutACHFormStripe_viewer.graphql';
import { CheckoutACHFormStripe_buyer$key } from './__generated__/CheckoutACHFormStripe_buyer.graphql';
import { CheckoutACHFormStripe_transaction$key } from './__generated__/CheckoutACHFormStripe_transaction.graphql';

type updateType = {
    field: string;
    value: string;
};

type achFormUpdateType = {
    field: string;
    value: string;
    isVerifying: boolean;
};

type Address = {
    city: string;
    country: string;
    firstName: string;
    lastName: string;
    line1: string;
    line2?: string;
    state: string;
    zipCode: string;
};

type Props = {
    viewer: CheckoutACHFormStripe_viewer$key;
    transaction: CheckoutACHFormStripe_transaction$key;
    buyer: CheckoutACHFormStripe_buyer$key;
    relay?: RelayProp;
};

const CheckoutACHFormStripe: FunctionComponent<Props> = props => {
    const { relay = {}, viewer: viewerRef, buyer: buyerRef, transaction: transactionRef } = props;

    const viewer = useFragment(
        graphql`
            fragment CheckoutACHFormStripe_viewer on Viewer {
                ...ControlledAddressForm_viewer
            }
        `,
        viewerRef
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutACHFormStripe_transaction on Transaction {
                ...CheckoutStripeForm_transaction
            }
        `,
        transactionRef
    );

    const buyer = useFragment(
        graphql`
            fragment CheckoutACHFormStripe_buyer on User {
                ...CheckoutEoriVatForm_buyer
                isTrade: isVerifiedTrade
                wallet {
                    paymentMethods {
                        paymentMethod {
                            ... on ACH {
                                token
                                last4
                                accountType
                                routingNumber
                            }
                        }
                    }
                }
            }
        `,
        buyerRef
    );

    const dispatch = useAppDispatch();

    const isVerifying = isVerifyingBankAccount(useCheckoutSelector(state => state.payment));
    const achShowFailedSubmissionError = useCheckoutSelector(
        state => state.payment.achShowFailedSubmissionError
    );
    const isPaymentSaving = useCheckoutSelector(state => state.payment.isPaymentSaving);
    const shippingCountry = useCheckoutSelector(state => state.shipping.defaultCountry);
    const newBillingForm = useCheckoutSelector(state => state.payment.newBillingForm);
    const isBillingSameAsShipping = useCheckoutSelector(
        state => state.payment.isBillingSameAsShipping
    );

    const onToggle = (checked: boolean): void => {
        dispatch(toggleBillingAddress(checked));
    };

    const onUpdate = ({ field, value }: updateType): void => {
        dispatch(achFormUpdated({ field, value, isVerifying }));
    };

    const foreignShippingCountry = shippingCountry !== 'US';
    const isTrade = !!buyer?.isTrade;

    const showAddressForm = !isBillingSameAsShipping || foreignShippingCountry || isVerifying;

    return (
        <Fragment>
            <CheckoutStripeForm transaction={transaction} />
            <div className={styles.achAddressForm}>
                {!foreignShippingCountry && (
                    <Checkbox
                        name="isBillingSameAsShipping"
                        label={
                            <FormattedMessage
                                id="checkout.CheckoutACHFormStripe.sameAsShipping"
                                defaultMessage="Billing Address is the Same as Shipping Address"
                            />
                        }
                        checked={isBillingSameAsShipping && !isVerifying}
                        disabled={isVerifying || isPaymentSaving}
                        wrapperClass={styles.stripeBillingAddressCheckbox}
                        onChange={onToggle}
                        dataTn="same-as-shipping-address-checkbox"
                    />
                )}
                {showAddressForm && (
                    <>
                        {foreignShippingCountry && (
                            <div className={styles.foreignBillingCaption}>
                                <FormattedMessage
                                    id="checkout.CheckoutACHFormStripe.foreignBillingAddressTitle"
                                    defaultMessage="Billing Address"
                                />
                            </div>
                        )}
                        <div className={styles.achAddressFormFields}>
                            <ControlledAddressForm
                                relay={relay}
                                viewer={viewer}
                                address={newBillingForm}
                                hideCompany
                                hidePhoneNumber
                                hideSubmit
                                validations={{ allowPOBox: true }}
                                disableForm={isPaymentSaving}
                                onUpdate={onUpdate}
                                showAllErrors={achShowFailedSubmissionError}
                            />
                        </div>
                    </>
                )}
                {isTrade && <CheckoutEoriVatForm buyer={buyer} hideEori />}
            </div>
        </Fragment>
    );
};

export default CheckoutACHFormStripe;
